import { Dispatch, SetStateAction, useEffect, useState, cloneElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { NavigationService } from '../../services/navigation.service';
import { GlobalService } from '../../services/global.service';

import { UserService } from '../../services/user.service';
import { AvatarService } from '../../services/avatar.service';
import { ToastService } from '../../services/toast.service';

import { Ability, Client, CommonGlobalService, CommonInfoService, dayjs, FORMAT_TIME_API, RootState, Sector, Site, Skill, UserUmanforge, WeekDaysButtons, APPS_NAME, getAppsFromMask } from '@umanforge/common';
import { APPS_INFOS } from 'constants/app.constants';
import { ModifyUser } from 'interfaces/api_user.interfaces';

import Switch from 'react-switch';
import { ModifyUserFileds } from '../../components/users/modify_user_fields.component';
import { ModifyUserLinks } from '../../components/users/modify_user_links.component';
import { Button } from '../../components/inputs/button.component';
import { Loading } from '../../components/loading.component';
import { BaseModal } from 'components/modals/base_modal.component';
import { ChangePasswordComponent } from './change_password.component';
import { BodyConfirmModal } from 'components/modals/body/body_confirm_modal.component';
import { ErrorModal } from 'components/modals/error_modal.component';

import { SaveIcon } from '../../components/icons/save_icon.component';
import { BinIcon } from '../../components/icons/bin_icon.component';
import { CircleXmarkIcon } from '../../components/icons/circle_xmark_icon.component';
import { CircleCheckIcon } from '../../components/icons/circle_check_icon.component';
import { WarningIcon } from 'components/icons/warning_icon.component';
import { InfoIcon } from 'components/icons/info_icon.component';
import { Tooltip } from 'react-tooltip';
import { Breadcrumb } from 'components/menu/breadcrumb.component';
import { UserIcon } from 'components/icons/user_icon.component';
import { UsersIcon } from 'components/icons/users_icon.component';
import { ROUTES, USER_ID } from 'constants/navigation.constants';
import { DEFAULT_SEND_MAIL_DAYS, DEFAULT_SEND_MAIL_TIME } from 'constants/user.constants';
import { TimePickerComponent } from '@umanforge/common';
import { InfoModal } from 'components/modals/info_modal.component';
import { useDispatch, useSelector } from 'react-redux';
import { isAdminOfAllClientsUser } from './modify_user.helpers';

const toastService: ToastService = new ToastService( 'Details User' );

interface ModifyUserProps {
  user: UserUmanforge | null,
  refreshUser: () => Promise<void>;
  needConfirmation: boolean;
  setNeedConfirmation: Dispatch<SetStateAction<boolean>>;
  avatarFile?: File | null;
  avatarChanged?: boolean;
  avatarInitialUrl?: string | null;
  avatarClientId?: string;
  canEditAvatar: boolean;
  onAvatarFileChange: ( file: File | null ) => void;
  onAvatarInitialUrlChange: ( url: string | null ) => void;
  onAvatarChanged: ( changed: boolean ) => void;
}

function getUserChangeValidation( {
  selectedClients,
  selectedSites,
  selectedSectors,
  modifyUser,
  changedClients,
  changedSites,
  changedSectors,
  changedSkills,
  changedAbilities,
  avatarChanged
}: {
  selectedClients: Client[];
  selectedSites: Site[];
  selectedSectors: Sector[];
  modifyUser: ModifyUser;
  changedClients: { added: { user: Client[], admin: Client[] }, removed: { user: Client[], admin: Client[] } };
  changedSites: { added: Site[], removed: Site[] };
  changedSectors: { added: { user: Sector[], admin: Sector[] }, removed: { user: Sector[], admin: Sector[] } };
  changedSkills: { added: Skill[], removed: Skill[] };
  changedAbilities: { added: Ability[], removed: Ability[] };
  avatarChanged?: boolean;
} ): { canPut: boolean; needConfirmation: boolean } {
  const haveClientsAlone = selectedClients.filter( selectedClient => { return !selectedSites.find( site => { return site.clientId === selectedClient.id; } ); } ).length > 0;
  const haveSitesAlone = selectedSites.filter( selectedSite => { return !selectedSectors.find( sector => { return sector.siteId === selectedSite.id; } ); } ).length > 0;
  const requirement = (
    modifyUser.firstname != '' && modifyUser.lastname != '' && modifyUser.email != '' &&
    !haveClientsAlone && !haveSitesAlone && ( modifyUser.sendMailDays == null || modifyUser.sendMailDays > 0 )
  );
  const isUserChange: boolean = (
    modifyUser.firstname != undefined ||
    modifyUser.lastname != undefined ||
    ( modifyUser.email != undefined && GlobalService.validateEmail( modifyUser.email ) ) ||
    modifyUser.language != undefined ||
    modifyUser.timezone != undefined ||
    modifyUser.sendMailTime !== undefined ||
    modifyUser.sendMailDays !== undefined ||
    modifyUser.readAccessSectors !== undefined ||
    modifyUser.appAccess !== undefined
  );
  const isClientsChange: boolean = changedClients.added.user.length > 0 || changedClients.added.admin.length > 0 || changedClients.removed.user.length > 0 || changedClients.removed.admin.length > 0;
  const isSitesChange: boolean = changedSites.added.length > 0 || changedSites.removed.length > 0;
  const isSectorsChange: boolean = changedSectors.added.user.length > 0 || changedSectors.added.admin.length > 0 || changedSectors.removed.user.length > 0 || changedSectors.removed.admin.length > 0;
  const isSkillsChange: boolean = changedSkills.added.length > 0 || changedSkills.removed.length > 0;
  const isAbilitiesChange: boolean = changedAbilities.added.length > 0 || changedAbilities.removed.length > 0;
  const isAvatarChange: boolean = !!avatarChanged;

  const canPut = requirement && (
    isUserChange ||
    isClientsChange ||
    isSitesChange ||
    isSectorsChange ||
    isSkillsChange ||
    isAbilitiesChange ||
    isAvatarChange
  );

  const needConfirmation = isUserChange ||
    isClientsChange ||
    isSitesChange ||
    isSectorsChange ||
    isSkillsChange ||
    isAbilitiesChange ||
    isAvatarChange;

  return { canPut, needConfirmation };
}

export function ModifyUserComponent( props: ModifyUserProps ) {
  const { user, refreshUser, needConfirmation, setNeedConfirmation, avatarFile, avatarChanged, avatarInitialUrl, avatarClientId, canEditAvatar, onAvatarFileChange, onAvatarInitialUrlChange, onAvatarChanged } = props;
  const { t } = useTranslation();
  const params = useParams();

  dayjs.extend( customParseFormat );

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );

  const defaultChangedAdminOptions = { added: { user: [], admin: [] }, removed: { user: [], admin: [] } };
  const defaultChangedUserOptions = { added: [], removed: [] };

  /*----------------------------- useState ------------------------------*/
  const [ selectedClients, setSelectedClients ] = useState<Client[]>( user ? CommonGlobalService.getUserClients( user ) : [] );
  const [ selectedSites, setSelectedSites ] = useState<Site[]>( user ? CommonGlobalService.getUserSites( user ) : [] );
  const [ selectedSectors, setSelectedSectors ] = useState<Sector[]>( user ? CommonGlobalService.getUserSectors( user ) : [] );
  const [ sendMailTime, setSendMailTime ] = useState<dayjs.Dayjs>( dayjs( DEFAULT_SEND_MAIL_TIME, FORMAT_TIME_API ) );
  const [ sendMailDays, setSendMailDays ] = useState<number>( DEFAULT_SEND_MAIL_DAYS );

  const [ readAccessSectors, setReadAccessSectors ] = useState( user?.readAccessSectors ?? false );
  const [ useSendMail, setUseSendMail ] = useState( user ? user.sendMailTime != null : false );

  const [ modifyUser, setModifyUser ] = useState<ModifyUser>( {} );
  const [ changedClients, setChangedClients ] = useState<{ added: { user: Client[], admin: Client[] }, removed: { user: Client[], admin: Client[] } }>( defaultChangedAdminOptions );
  const [ changedSites, setChangedSites ] = useState<{ added: Site[], removed: Site[]}>( defaultChangedUserOptions );
  const [ changedSectors, setChangedSectors ] = useState<{ added: { user: Sector[], admin: Sector[] }, removed: { user: Sector[], admin: Sector[] } }>( defaultChangedAdminOptions );
  const [ changedAbilities, setChangedAbilities ] = useState<{ added: Ability[], removed: Ability[]}>( defaultChangedUserOptions );
  const [ changedSkills, setChangedSkills ] = useState<{ added: Skill[], removed: Skill[]}>( defaultChangedUserOptions );
  const [ canPut, setCanPut ] = useState( false );

  const [ confirmSendMailModalIsOpen, setConfirmSendMailModalIsOpen ] = useState( false );
  const [ confirmDeleteModalIsOpen, setConfirmDeleteModalIsOpen ] = useState( false );
  const [ changePasswordModalIsOpen, setChangePasswordModalIsOpen ] = useState( false );

  const [ isLoading, setIsLoading ] = useState<boolean>( false );
  const [ appAccessMask, setAppAccessMask ] = useState<number>( user?.appAccess ?? 0 );

  // helper moved to ./modify_user.helpers.ts

  /*----------------------------- useEffect -----------------------------*/
  useEffect( () => {
    if ( user ) {
      setModifyUser( {} );
      setSendMailTime( dayjs( user.sendMailTime ?? DEFAULT_SEND_MAIL_TIME, FORMAT_TIME_API ) );
      setSendMailDays( user.sendMailDays ?? DEFAULT_SEND_MAIL_DAYS );
      setChangedClients( defaultChangedAdminOptions );
      setChangedSites( defaultChangedUserOptions );
      setChangedSectors( defaultChangedAdminOptions );
      setChangedAbilities( defaultChangedUserOptions );
      setChangedSkills( defaultChangedUserOptions );
      setAppAccessMask( user.appAccess ?? 0 );
    }
  }, [ user ] );

  useEffect( () => {
    if ( user ) {
      checkCanPut();
    }
  }, [ modifyUser, changedClients, changedSites, changedSectors, changedAbilities, changedSkills, avatarChanged ] );


  /*------------------------- Requetes API User -------------------------*/
  const deleteUser = (): Promise<boolean> => {
    return new Promise ( async resolve => {
      let isDeleted = false;
      const onWarningOrError = () => { resolve( false ); };
      setIsLoading( true );
      if ( user ) {
        isDeleted = await UserService.deleteUser( user.id, token, dispatch, onWarningOrError, onWarningOrError );
      }
      if ( isDeleted ) {
        toastService.showInfo( t( '{0}\'s account has been delete.' ).replace( '{0}', ( user?.fullname ?? t( 'The user' ) ) ) );
        NavigationService.toUsers( navigate, false );
      }
      setIsLoading( false );
      resolve( isDeleted );
    } );
  };

  const putUser = ( needSendMail: boolean ): Promise<boolean> => {
    return new Promise ( async resolve => {
      let isUpdated = false;
      const onWarningOrError = () => { resolve( false ); };
      if ( user && Object.entries( modifyUser ).find( item => { return item[ 1 ] !== undefined; } ) != null ) {
        isUpdated = await UserService.putUser( user.id, modifyUser, token, dispatch, onWarningOrError, onWarningOrError );
        if ( modifyUser.email && needSendMail ) {
          await UserService.resetPasswordUser( user.id, token, dispatch, onWarningOrError, onWarningOrError );
        }
      }
      resolve( isUpdated );
    } );
  };

  const removedUserToClients = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      const onWarningOrError = () => { resolve( false ); };
      if ( user?.id && changedClients.removed.user.length > 0 ) {
        const body = { clients: changedClients.removed.user.map( client => { return client.id; } ) };
        isUpdated = await UserService.deleteUserToClients( user?.id, body, token, dispatch, onWarningOrError, onWarningOrError );
      }
      resolve( isUpdated );
    } );
  };

  const addAdminToClients = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      const onWarningOrError = () => { resolve( false ); };
      if ( user?.id && changedClients.added.admin.length > 0 ) {
        const body = { clients: changedClients.added.admin.map( client => { return client.id; } ) };
        isUpdated = await UserService.postAdminToClients( user?.id, body, token, dispatch, onWarningOrError, onWarningOrError );
      }
      resolve( isUpdated );
    } );
  };

  const removedAdminToClients = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      const onWarningOrError = () => { resolve( false ); };
      if ( user?.id && changedClients.removed.admin.length > 0 ) {
        const body = { clients: changedClients.removed.admin.map( client => { return client.id; } ) };
        isUpdated = await UserService.deleteAdminToClients( user?.id, body, token, dispatch, onWarningOrError, onWarningOrError );
      }
      resolve( isUpdated );
    } );
  };

  const addAdminToSites = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      const onWarningOrError = () => { resolve( false ); };
      if ( user?.id && changedSites.added.length > 0 ) {
        const body = { sites: changedSites.added.map( site => { return site.id; } ) };
        isUpdated = await UserService.postAdminToSites( user?.id, body, token, dispatch, onWarningOrError, onWarningOrError );
      }
      resolve( isUpdated );
    } );
  };

  const removedAdminToSites = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      const onWarningOrError = () => { resolve( false ); };
      if ( user?.id && changedSites.removed.length > 0 ) {
        const body = { sites: changedSites.removed.map( site => { return site.id; } ) };
        isUpdated = await UserService.deleteAdminToSites( user?.id, body, token, dispatch, onWarningOrError, onWarningOrError );
      }
      resolve( isUpdated );
    } );
  };

  const addUserToSectors = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      const onWarningOrError = () => { resolve( false ); };
      if ( user?.id && changedSectors.added.user.length > 0 ) {
        const body = { sectors: changedSectors.added.user.map( sector => { return sector.id; } ) };
        isUpdated = await UserService.postUserToSectors( user?.id, body, token, dispatch, onWarningOrError, onWarningOrError );
      }
      resolve( isUpdated );
    } );
  };

  const removedUserToSectors = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      const onWarningOrError = () => { resolve( false ); };
      if ( user?.id && changedSectors.removed.user.length > 0 ) {
        const body = { sectors: changedSectors.removed.user.map( sector => { return sector.id; } ) };
        isUpdated = await UserService.deleteUserToSectors( user?.id, body, token, dispatch, onWarningOrError, onWarningOrError );
      }
      resolve( isUpdated );
    } );
  };

  const addAdminToSectors = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      const onWarningOrError = () => { resolve( false ); };
      if ( user?.id && changedSectors.added.admin.length > 0 ) {
        const body = { sectors: changedSectors.added.admin.map( sector => { return sector.id; } ) };
        isUpdated = await UserService.postAdminToSectors( user?.id, body, token, dispatch, onWarningOrError, onWarningOrError );
      }
      resolve( isUpdated );
    } );
  };

  const removedAdminToSectors = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      const onWarningOrError = () => { resolve( false ); };
      if ( user?.id && changedSectors.removed.admin.length > 0 ) {
        const body = { sectors: changedSectors.removed.admin.map( sector => { return sector.id; } ) };
        isUpdated = await UserService.deleteAdminToSectors( user?.id, body, token, dispatch, onWarningOrError, onWarningOrError );
      }
      resolve( isUpdated );
    } );
  };

  const addAbilitiesToUser = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      const onWarningOrError = () => { resolve( false ); };
      if ( user?.id && changedAbilities.added.length > 0 ) {
        const body = {
          abilities: changedAbilities.added.map( ability => { return {
            id: ability.id,
            expirationDate: ability.expirationDate ?? moment().add( 1, 'days' ).startOf( 'day' ).toISOString()
          }; } )
        };
        isUpdated = await UserService.postUserToAbilities( user?.id, body, token, dispatch, onWarningOrError, onWarningOrError );
      }
      resolve( isUpdated );
    } );
  };

  const removeAbilitiesToUser = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      const onWarningOrError = () => { resolve( false ); };
      if ( user?.id && changedAbilities.removed.length > 0 ) {
        const body = { abilities: changedAbilities.removed.map( ability => { return ability.id; } ) };
        isUpdated = await UserService.deleteUserToAbilities( user?.id, body, token, dispatch, onWarningOrError, onWarningOrError );
      }
      resolve( isUpdated );
    } );
  };

  const addSkillsToUser = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      const onWarningOrError = () => { resolve( false ); };
      if ( user?.id && changedSkills.added.length > 0 ) {
        const body = {
          skills: changedSkills.added.map( skill => { return {
            id: skill.id,
            value: skill.value ?? 1
          }; } )
        };
        isUpdated = await UserService.postUserToSkills( user?.id, body, token, dispatch, onWarningOrError, onWarningOrError );
      }
      resolve( isUpdated );
    } );
  };

  const removeSkillsToUser = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      const onWarningOrError = () => { resolve( false ); };
      if ( user?.id && changedSkills.removed.length > 0 ) {
        const body = { skills: changedSkills.removed.map( skill => { return skill.id; } ) };
        isUpdated = await UserService.deleteUserToSkills( user?.id, body, token, dispatch, onWarningOrError, onWarningOrError );
      }
      resolve( isUpdated );
    } );
  };

  /*----------------------------- Fonctions -----------------------------*/
  const onSaveClick = async () => {
    if ( modifyUser.email ) {
      openSendMailModal();
    } else {
      await saveUser( false );
    }
  };

  const openSendMailModal = () => {
    setConfirmSendMailModalIsOpen( true );
  };
  const closeSendMailModal = () => {
    setConfirmSendMailModalIsOpen( false );
  };
  const onCancelSendMailModal = async () => {
    await saveUser( false );
    closeSendMailModal();
  };
  const onConfirmSendMailModal = async () => {
    await saveUser( true );
    closeSendMailModal();
  };

  const saveUser = async ( needSendMail: boolean ) => {
    let isUserModify = false;
    setIsLoading( true );
    if ( user ) {
      const isPutUser = await putUser( needSendMail );
      const isAddAdminToClients = await addAdminToClients();
      const isAddAdminToSites = await addAdminToSites();
      const isAddUserToSectors = await addUserToSectors();
      const isAddAdminToSectors = await addAdminToSectors();
      const isAddAbilitiesToUser = await addAbilitiesToUser();
      const isAddSkillsToUser = await addSkillsToUser();
      const isRemoveAbilitiesToUser = await removeAbilitiesToUser();
      const isRemoveSkillsToUser = await removeSkillsToUser();
      const isRemovedAdminToClients = await removedAdminToClients();
      const isRemovedUserToClients = await removedUserToClients();
      const isRemovedAdminToSites = await removedAdminToSites();
      const isRemovedAdminToSectors = await removedAdminToSectors();
      const isRemovedUserToSectors = await removedUserToSectors();
      isUserModify = isPutUser ||
        isRemovedUserToClients ||
        isAddAdminToClients ||
        isRemovedAdminToClients ||
        isAddAdminToSites ||
        isRemovedAdminToSites ||
        isAddUserToSectors ||
        isRemovedUserToSectors ||
        isAddAdminToSectors ||
        isRemovedAdminToSectors ||
        isAddAbilitiesToUser ||
        isRemoveAbilitiesToUser ||
        isAddSkillsToUser ||
        isRemoveSkillsToUser;
    }
    if ( isUserModify ) {
      toastService.showInfo( t( '{0}\'s account has been modify.' ).replace( '{0}', ( user?.fullname ?? t( 'The user' ) ) ) );
    }
    // Handle avatar change: upload new file or delete existing avatar
    if ( avatarChanged && user ) {
      // Prefer the user's first client, then avatarClientId (from view), then selectedClients, then current user clients
      const clientId = ( user.clients && user.clients.length > 0 && user.clients[ 0 ].id )
        ? user.clients[ 0 ].id
        : ( avatarClientId && avatarClientId.length > 0 )
          ? avatarClientId
          : ( selectedClients && selectedClients.length > 0 && selectedClients[ 0 ].id )
            ? selectedClients[ 0 ].id
            : ( me && me.clients && me.clients[ 0 ] ? me.clients[ 0 ].id : '' );

      if ( !clientId ) {
        toastService.showWarning( t( 'No client found to upload avatar.' ) );
      } else {
        if ( avatarFile ) {
          const file: File = avatarFile;
          await AvatarService.uploadAvatar( user.id, clientId, file, token, dispatch );
        } else {
          await AvatarService.deleteAvatar( user.id, clientId, token, dispatch );
        }
      }
    }
    await refreshUser();
    setIsLoading( false );
  };

  const checkCanPut = (): void => {
    const result = getUserChangeValidation( {
      selectedClients,
      selectedSites,
      selectedSectors,
      modifyUser,
      changedClients,
      changedSites,
      changedSectors,
      changedSkills,
      changedAbilities,
      avatarChanged
    } );

    setCanPut( result.canPut );
    setNeedConfirmation( result.needConfirmation );
  };

  const onReadAccessSectorsChange = ( newValue: boolean ) => {
    const newReadAccessSectors = newValue;
    if ( user && newReadAccessSectors === user.readAccessSectors ) {
      delete modifyUser.readAccessSectors;
    } else {
      modifyUser.readAccessSectors = newReadAccessSectors;
    }
    setReadAccessSectors( newValue );
    setModifyUser( structuredClone( modifyUser ) );
  };

  const onSendMailTimeChange = ( newValue: string | null ) => {
    const time = newValue ? dayjs( newValue, FORMAT_TIME_API ) : null;
    if ( user && time?.format( FORMAT_TIME_API ) === user.sendMailTime ) {
      delete modifyUser.sendMailTime;
    } else {
      modifyUser.sendMailTime = time?.format( FORMAT_TIME_API ) ?? null;
    }
    if ( time ) {
      setSendMailTime( time );
    }
    setModifyUser( structuredClone( modifyUser ) );
  };

  const onSendMailDaysChange = ( newValue: number | null ) => {
    if ( user && newValue === user.sendMailDays ) {
      delete modifyUser.sendMailDays;
    } else {
      modifyUser.sendMailDays = newValue;
    }
    if ( newValue != null ) {
      setSendMailDays( newValue );
    }
    setModifyUser( structuredClone( modifyUser ) );
  };

  const appNameToBit = ( app: APPS_NAME ): number => {
    switch ( app ) {
    case APPS_NAME.FLASHMEETING: return 0b1000;
    case APPS_NAME.KAMISHIBAI: return 0b0100;
    case APPS_NAME.RESOLUTION: return 0b0010;
    case APPS_NAME.PLANITEAM: return 0b0001;
    default: return 0;
    }
  };

  const onToggleAppAccess = ( app: APPS_NAME, checked: boolean ) => {
    const bit = appNameToBit( app );
    const newMask = checked ? ( appAccessMask | bit ) : ( appAccessMask & ~bit );
    setAppAccessMask( newMask );
    if ( user && ( user.appAccess ?? 0 ) === newMask ) {
      delete modifyUser.appAccess;
    } else {
      modifyUser.appAccess = newMask;
    }
    setModifyUser( structuredClone( modifyUser ) );
  };

  const onClientsChange = ( selectClients: Client[] ) => {
    if ( user ) {
      setSelectedClients( oldSelectedClient => {
        if (
          [ ...oldSelectedClient ].sort().map( client => { return client.id + client.isAdmin; } ).join() !=
          [ ...selectClients ].sort().map( client => { return client.id + client.isAdmin; } ).join()
        ) {
          const userClients = CommonGlobalService.getUserClients( user );
          const items = {
            added: {
              admin: selectClients
                .filter( client => {
                  return ( !userClients.find( userClient => { return ( userClient.id === client.id ); } ) && client.isAdmin === true ) ||
                  ( userClients.find( userClient => { return ( userClient.id === client.id && ( userClient.isAdmin === false || userClient.isAdmin == null ) ) && client.isAdmin === true; } ) );
                } ),
              user: selectClients
                .filter( client => {
                  return ( !userClients.find( userClient => { return ( userClient.id === client.id ); } ) && ( client.isAdmin === false || client.isAdmin == null ) );
                } )
            },
            removed: {
              admin: userClients
                .filter( userClient => {
                  return selectClients.find( client => { return ( client.id === userClient.id && ( client.isAdmin === false || client.isAdmin == null ) ); } ) && userClient.isAdmin === true ;
                } ),
              user: userClients
                .filter( userClient => {
                  return !selectClients.find( client => { return ( client.id === userClient.id ); } );
                } )
            }
          };

          const clientsRemoved = items.removed.user;
          if ( clientsRemoved.length > 0 ) {

            const getRemovedSites = ( sites: Site[] ) => {
              return sites.filter( site => {
                return !clientsRemoved.some( client => { return site.clientId == client.id; } );
              } );
            };
            const getRemovedSectors = ( sectors: Sector[] ) => {
              return sectors.filter( sector => {
                const sites = CommonGlobalService.getUserSites( user );
                return !clientsRemoved.some( client => {
                  return sites.some( site => {
                    return (
                      site.clientId === client.id &&
                      sector.siteId === site.id
                    );
                  } );
                } );
              } );
            };

            changedSites.removed = getRemovedSites( changedSites.removed );
            changedSectors.removed.admin = getRemovedSectors( changedSectors.removed.admin );
            changedSectors.removed.user = getRemovedSectors( changedSectors.removed.user );
          }

          setChangedClients( items );
        }
        return structuredClone( selectClients );
      } );
    }
  };

  const onSitesChange = ( selectSites: Site[] ) => {
    if ( user ) {
      setSelectedSites( oldSelectedSite => {
        if (
          [ ...oldSelectedSite ].sort().map( site => { return site.id + site.isAdmin; } ).join() !=
          [ ...selectSites ].sort().map( site => { return site.id + site.isAdmin; } ).join()
        ) {
          const userSites = CommonGlobalService.getUserSites( user );
          const items = {
            added: selectSites
              .filter( site => {
                return ( !userSites.find( userSite => { return ( userSite.id === site.id ); } ) && site.isAdmin === true ) ||
                  ( userSites.find( userSite => { return ( userSite.id === site.id && ( userSite.isAdmin === false || userSite.isAdmin == null ) ) && site.isAdmin === true; } ) );
              } ),
            removed: userSites
              .filter( userSite => {
                return (
                  ( selectSites.find( site => { return ( site.id === userSite.id && ( site.isAdmin === false || site.isAdmin == null ) ); } ) && userSite.isAdmin === true ) ||
                  ( !selectSites.find( site => { return ( site.id === userSite.id ); } ) && userSite.isAdmin === true )
                ) &&
                !changedClients.removed.user.some( client => { return userSite.clientId === client.id; } );
              } )
          };
          setChangedSites( items );
        }
        return structuredClone( selectSites );
      } );
    }
  };

  const onSectorsChange = ( selectSectors: Sector[] ) => {
    if ( user ) {
      setSelectedSectors( oldSelectedSectors => {
        if (
          [ ...oldSelectedSectors ].sort().map( sector => { return sector.id + sector.isAdmin; } ).join() !=
          [ ...selectSectors ].sort().map( sector => { return sector.id + sector.isAdmin; } ).join()
        ) {
          const userSectors = CommonGlobalService.getUserSectors( user );
          const userSites = CommonGlobalService.getUserSites( user );
          const items = {
            added: {
              admin: selectSectors
                .filter( sector => {
                  return ( !userSectors.find( userSector => { return ( userSector.id === sector.id ); } ) && sector.isAdmin === true ) ||
                    ( userSectors.find( userSector => { return ( userSector.id === sector.id && ( userSector.isAdmin === false || userSector.isAdmin == null ) ) && sector.isAdmin === true; } ) );
                } ),
              user: selectSectors
                .filter( sector => {
                  return ( !userSectors.find( userSector => { return ( userSector.id === sector.id ); } ) && ( sector.isAdmin === false || sector.isAdmin == null ) );
                } )
            },
            removed: {
              admin: userSectors
                .filter( userSector => {
                  const findSite = ( site: Site ) => { return site.id === userSector.siteId; };
                  return (
                    selectSectors.find( sector => { return ( sector.id === userSector.id && ( sector.isAdmin === false || sector.isAdmin == null ) ); } ) && userSector.isAdmin === true ) &&
                    !changedClients.removed.user.some( client => {
                      return changedSites.removed.find( findSite )?.clientId === client.id;
                    } );
                } ),
              user: userSectors
                .filter( userSector => {
                  const findSite = ( site: Site ) => { return site.id === userSector.siteId; };
                  return (
                    !selectSectors.find( sector => { return ( sector.id === userSector.id ); } ) ) &&
                    !changedClients.removed.user.some( client => {
                      return userSites.find( findSite )?.clientId === client.id;
                    } );
                } )
            }
          };
          setChangedSectors( items );
        }
        return structuredClone( selectSectors );
      } );
    }
  };

  const onAbilitiesChange = ( selectAbilities: Ability[] ) => {
    if ( user ) {
      const userAbilities = GlobalService.getUserAbilities( user );
      const items = {
        added: selectAbilities.filter( ability => { return !userAbilities.find( userAbility => { return ( userAbility.id === ability.id ) && ( userAbility.expirationDate === ability.expirationDate ); } ); } ),
        removed: userAbilities.filter( userAbility => { return !selectAbilities.find( ability => { return ability.id === userAbility.id; } ); } )
      };
      setChangedAbilities( items );
    }
  };

  const onSkillsChange = ( selectSkills: Skill[] ) => {
    if ( user ) {
      const userSkills = GlobalService.getUserSkills( user );
      const items = {
        added: selectSkills.filter( skill => { return !userSkills.find( userSkill => { return ( userSkill.id === skill.id ) && ( userSkill.value === skill.value ); } ); } ),
        removed: userSkills.filter( userSkill => { return !selectSkills.find( skill => { return skill.id === userSkill.id; } ); } )
      };
      setChangedSkills( items );
    }
  };

  /*------------------------------- Front -------------------------------*/
  return (
    <>
      <Breadcrumb
        needConfirmationToNav={ needConfirmation }
        links={
          [
            { label: t( 'common:user' ), route: ROUTES.USERS, icon: ( <UsersIcon className='h-[14px] select-none cursor-pointer'/> ) },
            { label: user ? user.fullname : t( 'Modify user' ), route: ROUTES.USER.replace( USER_ID, params.userId ?? '' ), icon: ( <UserIcon className='h-[14px] select-none cursor-pointer'/> ) }
          ]
        }
      />
      {
        !isLoading && user ?
          <div className='flex flex-col items-center justify-center'>
            { user.isLock && user.isAdminOfUser ?
              <div className='flex w-fit px-8 py-4 my-4 gap-4 rounded-md items-center justify-center bg-red-500 fill-white text-white font-semibold'>
                <WarningIcon className='w-12 h-12'/>
                <div className='flex flex-col gap-2 items-center'>
                  { t( 'The user\'s account is locked !' ) }
                  <Button
                    label={ t( 'To unlock it' ) }
                    color=''
                    colorHover=''
                    onClick={ async () => {
                      if ( await UserService.unlockUser( user.id, token, dispatch ) ) {
                        refreshUser();
                      }
                    } }
                  />
                </div>
              </div>
              :
              null
            }
            <div className='relative flex flex-col gap-10 w-full p-4 mb-12'>

              <div className='flex flex-col xl:flex-row gap-4 xl:gap-12 h-full w-full items-center xl:items-start justify-center'>
                <ModifyUserFileds
                  user={ user }
                  onUserChange={ setModifyUser }
                  t={ t }
                  canEditAvatar={ canEditAvatar }
                  avatarInitialUrl={ avatarInitialUrl ?? null }
                  onAvatarFileChange={ onAvatarFileChange }
                  onAvatarInitialUrlChange={ onAvatarInitialUrlChange }
                  onAvatarChanged={ onAvatarChanged }
                />

                <ModifyUserLinks
                  user={ structuredClone( user ) }
                  onClientsChange={ onClientsChange }
                  onSitesChange={ onSitesChange }
                  onSectorsChange={ onSectorsChange }
                  onAbilitiesChange={ onAbilitiesChange }
                  onSkillsChange={ onSkillsChange }
                  t={ t }
                />
              </div>

              {
                isAdminOfAllClientsUser( me, user ) || user.id == me?.id ?
                  <div className='flex flex-col border-y-[1px] border-gray-base-light divide-gray-base-light divide-y'>

                    { /* Partie Email quotidien */ }
                    <div className='flex flex-col w-full py-4 px-6 gap-4 sm:gap-2'>
                      <div className='flex flex-col gap-2 sm:flex-row sm:gap-0 sm:items-center'>
                        <span className='flex w-full font-bold flex-col'>
                          <div className='flex items-center gap-2'>
                            <span>{ t( 'translation:sectors_access' ) }</span>
                            <div id={ 'sectors-access-info-' + ( user?.id ?? 'global' ) } className='flex items-center ml-2 z-10 pointer-events-auto'>
                              <InfoIcon className='w-4 h-4 text-gray-600 cursor-pointer' size={16} />
                            </div>
                            <Tooltip
                              anchorSelect={ '#sectors-access-info-' + ( user?.id ?? 'global' ) }
                              className='z-[1000] max-w-[600px] text-wrap'
                              place="top"
                              positionStrategy="fixed"
                            >
                              <span className='text-[13px]'>
                                { t( 'translation:only_client_admin_can_modify_this_field' ) }
                              </span>
                            </Tooltip>
                          </div>
                          <span className='text-[14px] font-normal text-gray-600'>
                            { t( 'translation:would_you_like_give_sectors_access' ) }
                          </span>
                        </span>
                        <div className='flex items-center'>
                          <Switch
                            className='sm:mx-6'
                            disabled={ !user.isAdminOfUser }
                            checked={ readAccessSectors }
                            onChange={ onReadAccessSectorsChange }
                          />
                        </div>
                      </div>
                    </div>

                    { /* Partie Application Access */ }
                    <div className='flex flex-col w-full py-4 px-6 gap-4 sm:gap-2'>
                      <div className='flex flex-col gap-2'>
                        <span className='flex w-full font-bold flex-col'>
                          <div className='flex items-center gap-2'>
                            <span>{ t( 'translation:application_access' ) }</span>
                            <div id={ 'application-access-info-' + ( user?.id ?? 'global' ) } className='flex items-center ml-2 z-10 pointer-events-auto'>
                              <InfoIcon className='w-4 h-4 text-gray-600 cursor-pointer' size={16} />
                            </div>
                            <Tooltip
                              anchorSelect={ '#application-access-info-' + ( user?.id ?? 'global' ) }
                              className='z-[1000] max-w-[600px] text-wrap'
                              place="top"
                              positionStrategy="fixed"
                            >
                              <span className='text-[13px]'>
                                { t( 'translation:only_client_admin_can_modify_this_field' ) }
                              </span>
                            </Tooltip>
                          </div>
                          <span className='text-[14px] font-normal text-gray-600'>
                            { t( 'translation:give_or_remove_application_access' ) }
                          </span>
                        </span>
                        <div className='flex flex-row flex-wrap gap-6 pt-2'>
                          {
                            Object.values( APPS_NAME ).map( appName => {
                              const apps = getAppsFromMask( appAccessMask );
                              const checked = apps.includes( appName as APPS_NAME );
                              const info = ( APPS_INFOS as Record<string, { name: string; color?: string; icon?: JSX.Element }> )[ appName as string ];
                              const appLabel = info?.name ?? String( appName );
                              const iconEl = info?.icon as React.ReactElement<{ className?: string }> | undefined;
                              return (
                                <div key={ appName } className='flex items-center gap-4 border-[1px] border-gray-base-light rounded-md px-4 py-2 flex-shrink-0'>
                                  <div className='flex items-center justify-center w-[48px] h-[48px] rounded-md text-white' style={ { background: info?.color ?? 'transparent' } }>
                                    {
                                      iconEl ? cloneElement( iconEl, { className: ( ( iconEl.props?.className ?? '' ) + ' fill-white' ).trim() } ) : null
                                    }
                                  </div>
                                  <span className='text-sm'>{ t( `common:apps.${ appLabel.toLowerCase() }` ) }</span>
                                  <Switch
                                    checked={ checked }
                                    onChange={ ( value: boolean ) => { onToggleAppAccess( appName as APPS_NAME, value ); } }
                                    disabled={ !user.isAdminOfUser }
                                  />
                                </div>
                              );
                            } )
                          }
                        </div>
                      </div>
                    </div>

                    {
                      user.id == me?.id ?
                        <>

                          { /* Partie Email quotidien */ }
                          <div className='flex flex-col w-full py-4 px-6 gap-4 sm:gap-2'>
                            <div className='flex flex-col gap-2 sm:flex-row sm:gap-0 sm:items-center'>
                              <span className='flex w-full font-bold flex-col'>
                                <div className='flex items-center gap-2'>
                                  <span>{ t( 'translation:daily_email' ) }</span>
                                  <div id={ 'daily-email-kamishibai-info-' + ( user?.id ?? 'global' ) } className='flex items-center ml-2 z-10 pointer-events-auto'>
                                    <InfoIcon className='w-4 h-4 text-gray-600 cursor-pointer' size={16} />
                                  </div>
                                  <Tooltip
                                    anchorSelect={ '#daily-email-kamishibai-info-' + ( user?.id ?? 'global' ) }
                                    className='z-[1000] max-w-[600px] text-wrap'
                                    place='top'
                                    positionStrategy='fixed'
                                  >
                                    <span className='text-[13px]'>
                                      { t( 'translation:kamishibai_feature_not_applicable' ) }
                                    </span>
                                  </Tooltip>
                                </div>
                                <span className='text-[14px] font-normal text-gray-600'>
                                  { t( 'translation:would_you_like_to_receive_a_daily_summary_email' ) }
                                </span>
                              </span>
                              <Switch
                                className='sm:mx-6'
                                checked={ useSendMail }
                                onChange={ isChecked => {
                                  if ( isChecked ) {
                                    onSendMailTimeChange( sendMailTime.format( FORMAT_TIME_API ) );
                                    onSendMailDaysChange( sendMailDays );
                                  } else {
                                    onSendMailTimeChange( null );
                                    onSendMailDaysChange( null );
                                  }
                                  setUseSendMail( isChecked );
                                } }
                              />
                            </div>
                            {
                              useSendMail && (
                                <div className='flex flex-col gap-4'>
                                  <div className='flex w-full gap-3 items-center'>
                                    <span className='min-w-max text-[14px] font-normal text-gray-600'>
                                      { t( 'translation:time_of_send' ) }
                                    </span>
                                    <div className='flex text-center'>
                                      <TimePickerComponent
                                        value={ sendMailTime }
                                        step={ 15 }
                                        onChange={ time => { onSendMailTimeChange( time.format( FORMAT_TIME_API ) ); } }
                                      />
                                    </div>
                                  </div>
                                  <div className='flex w-full gap-3 items-center'>
                                    <span className='min-w-max text-[14px] font-normal text-gray-600'>
                                      { t( 'translation:date_of_send' ) }
                                    </span>
                                    <div className='flex text-center'>
                                      <WeekDaysButtons
                                        defaultSelectedDays={ CommonGlobalService.getDaysFromBinary( sendMailDays ) }
                                        onSelectionChange={ weekDays => { onSendMailDaysChange( CommonGlobalService.getBinaryFromDays( weekDays ) ); } }
                                      />
                                    </div>
                                  </div>
                                </div>
                              )
                            }
                          </div>

                          { /* Partie Mot de passe */ }
                          <div className='flex flex-col w-full py-4 px-6 gap-4 sm:gap-2 sm:flex-row'>
                            <span className='flex w-full font-bold flex-col'>
                              { t( 'Password' ) }
                              <span className='text-[14px] font-normal text-gray-600'>
                                { t( 'Need to change your password ?' ) }
                              </span>
                            </span>
                            <span
                              className='text-blue-600 font-medium mx-6 self-center select-none cursor-pointer transition ease-in-out active:scale-90'
                              onClick={ () => { setChangePasswordModalIsOpen( true ); } }
                            >
                              { t( 'Edit' ) }
                            </span>
                          </div>

                        </>
                        :
                        null
                    }
                  </div>
                  :
                  null
              }
              {
                user.isAdminOfUser ?
                  <div className='flex justify-center w-full h-14 mt-10'>
                    {
                      user.id != me?.id && !user.isSuperAdmin ?
                        <>
                          <div className='h-fit w-1/3 min-w-[160px] max-w-[250px]'>
                            <Button
                              label={ t( 'Delete' ) }
                              icon={ <BinIcon className='h-[24px] w-[24px] stroke-white'/> }
                              color='#f87171'
                              colorHover='#ef4444'
                              onClick={ () => { setConfirmDeleteModalIsOpen( true ); } }
                            />
                          </div>
                          <div className='h-fit w-1/3 min-w-[160px] max-w-[250px]'>
                            <Button
                              label={ user.isDisable ? t( 'Enable' ) : t( 'Disable' ) }
                              icon={
                                user.isDisable ?
                                  <CircleCheckIcon className='h-[24px] w-[24px] fill-white'/>
                                  :
                                  <CircleXmarkIcon className='h-[24px] w-[24px] fill-white'/>
                              }
                              color={ user.isDisable ? '#6FA8DC' : '#f59e0b' }
                              colorHover={ user.isDisable ? '#5886b0' : '#d97706' }
                              onClick={ async () => {
                                let isChanged = false;
                                if ( user.isDisable ) {
                                  isChanged = await UserService.enableUser( user.id, token, dispatch );
                                } else {
                                  isChanged = await UserService.disableUser( user.id, token, dispatch );
                                }
                                if ( isChanged ) {
                                  toastService.showInfo( t( '{0}\'s account has been ' + ( user.isDisable ? 'enable' : 'disable' ) + '.' ).replace( '{0}', ( user?.fullname ?? t( 'The user' ) ) ) );
                                  await refreshUser();
                                }
                              } }
                            />
                          </div>
                        </>
                        :
                        null
                    }
                    <div className='h-fit w-1/3 min-w-[160px] max-w-[250px]'>
                      <Button
                        label={ t( 'save' ) }
                        disable={ !canPut }
                        icon={ <SaveIcon className='h-[23px] w-[23px] stroke-white'/> }
                        color='#0bb173'
                        colorHover='#088D5C'
                        onClick={ onSaveClick }
                      />
                    </div>
                  </div>
                  :
                  null
              }
            </div>

            { confirmDeleteModalIsOpen ?
              <ErrorModal
                title={ t( 'Deletion of {0} !' ).replace( '{0}', user.fullname )  }
                isOpen={ confirmDeleteModalIsOpen }
                onClose={ () => { setConfirmDeleteModalIsOpen( false ); } }
              >
                <BodyConfirmModal
                  onCancel={ () => { setConfirmDeleteModalIsOpen( false ); } }
                  onConfirm={ async () => {
                    const isDeleted = await deleteUser();
                    if ( isDeleted ) {
                      NavigationService.toUsers( navigate, false );
                    }
                    setConfirmDeleteModalIsOpen( false );
                  } }
                >
                  <span className='flex flex-col w-full h-full text-[14px] font-[475] text-gray-600 p-3'>
                    { t( 'Are you sure you want to delete this user ?' ) }
                  </span>
                </BodyConfirmModal>
              </ErrorModal>
              :
              null
            }
            { changePasswordModalIsOpen ?
              <BaseModal
                title={ t( 'Change your password' ) }
                isOpen={changePasswordModalIsOpen}
                onClose={ () => { setChangePasswordModalIsOpen( false ); } }
              >
                <div className='p-4'>
                  <ChangePasswordComponent
                    buttonText={ t( 'Save new password' ) }
                    onClick={ async password => {
                      const seed = await CommonInfoService.getAuthInfo( token, dispatch );
                      if ( seed ) {
                        const isChange = await UserService.putPasswordUser( user.id, password, seed, token, dispatch );
                        if ( isChange ) {
                          setChangePasswordModalIsOpen( false );
                          toastService.showSuccess( 'Your password has been changed' );
                        }
                      }
                    } }
                  />
                </div>
              </BaseModal>
              :
              null
            }
            { confirmSendMailModalIsOpen ?
              <InfoModal
                id={ 'confirm_seed_mail_modal' }
                title={ t( 'translation:reset_password_title' ) }
                isOpen={ confirmSendMailModalIsOpen }
                onClose={ close }
                hasCross={ false }
              >
                <BodyConfirmModal
                  confirmText={ t( 'common:yes' ) }
                  cancelText={ t( 'common:no' ) }
                  onConfirm={ onConfirmSendMailModal }
                  onCancel={ onCancelSendMailModal }
                  confirmColor='#4986f3'
                  confirmColorHover='#3579F2'
                >
                  <div className='p-2'>
                    { t( 'translation:reset_password_desc' ) }
                  </div>
                </BodyConfirmModal>
              </InfoModal>
              :
              null
            }
          </div>
          :
          <Loading/>
      }
    </>
  );

}